import React, { useState } from 'react';
import { Loader2, Merge } from 'lucide-react';
import { Button } from '../Button';
import { abschnittZusammenfuehrFrage } from './arbeitsschritteListeLogik';
import type { AbschnittZiel } from './arbeitsschritteListeLogik';

const ZIEL_PRAEFIX = 'ab:';

interface AbschnittKopfProps {
  name: string;
  schluessel: string;             // wie `abschnittVon` ('' = ohne Abschnitt)
  anzahl: number;                 // Schritte im Abschnitt
  ziele: AbschnittZiel[];         // alle Abschnitte; der eigene wird ausgeblendet
  bearbeitbar: boolean;           // false = Nur-Ansicht oder Auswahlmodus: nur der Titel
  gesperrt: boolean;              // ein Verschiebe-/Zusammenführ-Vorgang läuft
  fortschritt: { erledigt: number; gesamt: number } | null; // nur, solange DIESER Abschnitt verschoben wird
  /** true = vollständig verschoben, die Rückfrage schließt. */
  onZusammenfuehren: (ziel: string) => Promise<boolean>;
}

/**
 * Kopf einer Abschnittsgruppe. „Zusammenführen mit…“ verschiebt alle Schritte
 * des Abschnitts in einen anderen — erst nach Wahl des Ziels und
 * Inline-Bestätigung (kein window.confirm).
 */
export const AbschnittKopf: React.FC<AbschnittKopfProps> = ({
  name, schluessel, anzahl, ziele, bearbeitbar, gesperrt, fortschritt, onZusammenfuehren,
}) => {
  const [offen, setOffen] = useState(false);
  const [ziel, setZiel] = useState<string | null>(null);
  const auswahl = ziele.filter(z => z.schluessel !== schluessel);
  const zielName = ziel === null ? null : (auswahl.find(z => z.schluessel === ziel)?.name ?? null);
  const laeuft = !!fortschritt;

  const umschalten = () => {
    setOffen(!offen);
    setZiel(null);
  };

  const bestaetigen = async () => {
    if (ziel === null || gesperrt) return;
    if (await onZusammenfuehren(ziel)) {
      setOffen(false);
      setZiel(null);
    }
  };

  return (
    <div className="mb-1">
      <div className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded group">
        <span className="flex-1 min-w-0 text-[11px] font-bold text-slate-500 uppercase tracking-wider truncate" title={name}>
          {name}
        </span>
        {bearbeitbar && (
          <button
            onClick={umschalten}
            disabled={laeuft || (gesperrt && !offen)}
            className="shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 p-0.5 text-slate-400 hover:text-blue-500 hover:bg-white rounded disabled:opacity-40"
            title="Zusammenführen mit… (alle Arbeitsschritte in einen anderen Abschnitt verschieben)"
            aria-label={`Abschnitt „${name}“ zusammenführen mit…`}
            aria-expanded={offen}
          >
            <Merge className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {bearbeitbar && offen && (
        <div className="mt-1 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900 space-y-2">
          <label className="block">
            <span className="block font-semibold mb-1">„{name}“ zusammenführen mit…</span>
            <select
              value={ziel === null ? '' : ZIEL_PRAEFIX + ziel}
              onChange={e => setZiel(e.target.value.startsWith(ZIEL_PRAEFIX) ? e.target.value.slice(ZIEL_PRAEFIX.length) : null)}
              disabled={laeuft}
              className="w-full text-xs p-1.5 border border-blue-300 rounded bg-white outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="" disabled>Zielabschnitt wählen …</option>
              {auswahl.map(z => (
                <option key={ZIEL_PRAEFIX + z.schluessel} value={ZIEL_PRAEFIX + z.schluessel}>{z.name}</option>
              ))}
            </select>
          </label>
          {zielName !== null && (
            <p className="font-medium">{abschnittZusammenfuehrFrage(anzahl, name, zielName)}</p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" size="sm" onClick={bestaetigen} disabled={zielName === null || gesperrt}>
              {laeuft && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              Verschieben
            </Button>
            <Button variant="ghost" size="sm" onClick={umschalten} disabled={laeuft}>
              Abbrechen
            </Button>
            {fortschritt && fortschritt.gesamt > 0 && (
              <span className="text-[11px] text-blue-800">
                Verschiebe {fortschritt.erledigt} / {fortschritt.gesamt} …
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
